import React, { useState, useEffect } from "react";
import {
  StyleSheet,
  View,
  TouchableOpacity,
  Image,
  Platform,
  type StyleProp,
  type ViewStyle,
  type TextStyle,
} from "react-native";
import { router } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "../ThemedText";
import { ThemedToolTip } from "../ThemedToolTip";
import { ThemedPlatformSymbol } from "../ThemedPlatformSymbol";
import { ThemedKebabMenu, type IKebabActionItem } from "../ThemedKebabMenu";
import { useThemeColor } from "@/hooks/useThemeColor";
import { useComponentTheme } from "@/hooks/useComponentTheme";
import { CardEntityType, getMediaType } from "common247";
import { getEntityIcon, navigateToCardEntity } from "./card-helpers";

export interface ICompactSharedItem {
  entityType: "memory" | "media" | "contact" | "group";
  id?: string;
  title?: string;
  thumbnailUrl?: string;
  date?: string;
}

export interface IThemedCompactCardProps {
  /** The entity domain category */
  entityType: CardEntityType;
  /** Canonical entity ID */
  id?: string;
  /** Primary title (e.g. Memory title, Media name, Contact name) */
  title?: string;
  /** Optional date string (rendered formatted) */
  date?: string;
  /** Image thumbnail URL if available */
  thumbnailUrl?: string;
  /** Tag type for tag entities */
  tagType?: string;
  /** Explicit media type for media entities ('image' | 'video' | 'audio' | 'pdf' | 'document' | 'other') */
  mediaType?: string;
  /** Whether the user is the owner */
  isOwner?: boolean;
  /** Whether the item is the logged-on AppUser / self-contact */
  isSelf?: boolean;
  /** Whether the item has a 5-star rating */
  isFiveStar?: boolean;
  /** For share cards: the nested shared item representation */
  sharedItem?: ICompactSharedItem;
  /** For share cards: the recipient display name */
  recipientName?: string;
  /** Selection toggle / selection mode */
  selectionMode?: boolean;
  isSelected?: boolean;
  onSelect?: () => void;
  /** Click action callback */
  onPress?: () => void;
  /** Long press callback */
  onLongPress?: () => void;
  /** Context menu callback */
  onContextMenu?: (e?: any) => void;
  /** Contextual kebab menu actions */
  kebabActions?: IKebabActionItem[];
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

/**
 * ThemedCompactCard: Tier 2 Item Card component.
 *
 * Implements single-item display for contextual pickers/selectors and compact list modes:
 * - Contact: Name and small thumbnail image if available
 * - Group: Name and small thumbnail image if available
 * - Memory: Title, Date and small thumbnail image if available
 * - Media: Medium thumbnail image
 * - Tag: Name with icon of TagType
 * - Share: Shared Item (compact card) and Recipient Name
 */
export function ThemedCompactCard({
  entityType,
  id,
  title,
  date,
  thumbnailUrl,
  tagType,
  mediaType,
  isOwner = false,
  isSelf = false,
  isFiveStar = false,
  sharedItem,
  recipientName,
  selectionMode = false,
  isSelected = false,
  onSelect,
  onPress,
  onLongPress,
  onContextMenu,
  kebabActions,
  style,
  testID,
}: IThemedCompactCardProps): React.JSX.Element {
  const [isHovered, setIsHovered] = useState(false);
  const [hasMediaImageError, setHasMediaImageError] = useState(false);

  useEffect(() => {
    setHasMediaImageError(false);
  }, [thumbnailUrl]);

  const { card: cardTheme } = useComponentTheme();
  const cardBg = cardTheme.chrome.backgroundColor;
  const borderColor = cardTheme.chrome.restingBorderColor;
  const tintColor = cardTheme.chrome.selectedBorderColor;
  const textColor = cardTheme.chrome.textColor;
  const textMuted = cardTheme.chrome.textMutedColor;
  const badgeBg = cardTheme.chrome.badgeBgColor;

  const displayTitle = title || id || `${entityType} item`;

  const handleCardPress = () => {
    if (selectionMode && onSelect) {
      onSelect();
      return;
    }
    if (onPress) {
      onPress();
      return;
    }
    navigateToCardEntity(entityType, id, "ThemedCompactCard");
  };

  // Special rendering for Media Tier 2 Compact Card: Medium thumbnail image focus
  if (entityType === "media") {
    const detectedType = mediaType || getMediaType(undefined, thumbnailUrl || title);
    const isAudio = detectedType === "audio" || getMediaType(undefined, thumbnailUrl) === "audio";
    const isVideo = detectedType === "video" || getMediaType(undefined, thumbnailUrl) === "video";
    const isImageFile = /\.(jpe?g|png|gif|webp|bmp|heic)(\?.*)?$/i.test(thumbnailUrl || "");
    const canRenderImage = Boolean(thumbnailUrl) && !hasMediaImageError && !isAudio && (!isVideo || isImageFile);

    const getPlaceholderIcon = () => {
      if (isAudio) return "musical-notes-outline";
      if (isVideo) return "videocam-outline";
      return "image-outline";
    };

    return (
      <TouchableOpacity
        testID={testID}
        activeOpacity={0.8}
        onPress={handleCardPress}
        {...(Platform.OS === "web"
          ? ({
              onMouseEnter: () => setIsHovered(true),
              onMouseLeave: () => setIsHovered(false),
            } as any)
          : {})}
        style={[
          styles.mediaCompactCard,
          {
            backgroundColor: cardBg,
            borderColor: isSelected ? tintColor : isHovered ? tintColor : borderColor,
          },
          style,
        ]}
        accessibilityRole={Platform.select({ web: "article" as any, default: undefined })}
        accessibilityLabel={`Media item: ${displayTitle}`}
      >
        {canRenderImage ? (
          <Image
            source={{ uri: thumbnailUrl }}
            style={styles.mediaThumbnail}
            resizeMode="cover"
            onLoadStart={() => {
              if (Platform.OS === "android") {
                console.log(`[Media:Compact:LoadStart] [${id || "unknown"}] URI: ${thumbnailUrl?.substring(0, 100)}...`);
              }
            }}
            onLoad={() => {
              if (Platform.OS === "android") {
                console.log(`[Media:Compact:LoadSuccess] [${id || "unknown"}] "${displayTitle}"`);
              }
            }}
            onError={() => {
              setHasMediaImageError(true);
            }}
          />
        ) : (
          <View style={[styles.mediaPlaceholder, { backgroundColor: badgeBg }]}>
            <Ionicons
              name={getPlaceholderIcon()}
              size={24}
              color={tintColor}
            />
          </View>
        )}
        <View style={styles.mediaOverlayBottom}>
          <ThemedText numberOfLines={1} style={[styles.mediaOverlayTitle, { color: textColor }]}>
            {displayTitle}
          </ThemedText>
        </View>
        {/* Trailing platform symbols */}
        <View style={styles.mediaSymbolRow}>
          {isOwner && (
            <ThemedPlatformSymbol type="owner" size="small" />
          )}
          {isFiveStar && (
            <ThemedPlatformSymbol type="fivestar" size="small" />
          )}
        </View>
      </TouchableOpacity>
    );
  }

  // Special rendering for Share Tier 2 Compact Card: Shared Item + Recipient Name
  if (entityType === "share") {
    return (
      <TouchableOpacity
        testID={testID}
        activeOpacity={0.8}
        onPress={handleCardPress}
        {...(Platform.OS === "web"
          ? ({
              title: sharedItem?.title || displayTitle,
              onMouseEnter: () => setIsHovered(true),
              onMouseLeave: () => setIsHovered(false),
            } as any)
          : {})}
        style={[
          styles.container,
          {
            backgroundColor: cardBg,
            borderColor: isSelected ? tintColor : isHovered ? tintColor : borderColor,
          },
          style,
        ]}
        accessibilityRole={Platform.select({ web: "article" as any, default: undefined })}
        accessibilityLabel={`Share card: ${sharedItem?.title || displayTitle} shared with ${recipientName || "recipient"}`}
      >
        {/* Leading Shared Item Compact Snippet */}
        <View style={styles.shareItemSnippet}>
          {sharedItem?.thumbnailUrl ? (
            <Image
              source={{ uri: sharedItem.thumbnailUrl }}
              style={styles.avatarThumbnail}
              onError={(e) => {
                if (Platform.OS === "android") {
                  console.warn(`[Share:Compact:LoadError] Failed to load share thumbnail:`, {
                    sharedItem,
                    nativeError: e.nativeEvent?.error,
                  });
                }
              }}
            />
          ) : (
            <View style={[styles.avatarPlaceholder, { backgroundColor: badgeBg }]}>
              <Ionicons
                name={getEntityIcon(sharedItem?.entityType || "memory")}
                size={16}
                color={tintColor}
              />
            </View>
          )}
          <View style={styles.textColumn}>
            <ThemedText numberOfLines={2} type="defaultSemiBold" style={styles.titleText}>
              {sharedItem?.title || displayTitle}
            </ThemedText>
            <ThemedText numberOfLines={1} style={[styles.subText, { color: textMuted }]}>
              Recipient: {recipientName || "Direct Recipient"}
            </ThemedText>
          </View>
        </View>
      </TouchableOpacity>
    );
  }

  // Standard Compact Card for Contact, Group, Memory, Tag
  return (
    <TouchableOpacity
      testID={testID}
      activeOpacity={0.8}
      onPress={handleCardPress}
      onLongPress={onLongPress}
      delayLongPress={350}
      {...(Platform.OS === "web"
        ? ({
            title: displayTitle,
            onMouseEnter: () => setIsHovered(true),
            onMouseLeave: () => setIsHovered(false),
            onContextMenu: (e: any) => {
              if (onContextMenu) {
                e.preventDefault();
                onContextMenu(e);
              }
            },
          } as any)
        : {})}
      style={[
        styles.container,
        {
          backgroundColor: cardBg,
          borderColor: isSelected ? tintColor : isHovered ? tintColor : borderColor,
        },
        style,
      ]}
      accessibilityRole={Platform.select({ web: "article" as any, default: undefined })}
      accessibilityLabel={`${entityType} item: ${displayTitle}`}
    >
      {/* Selection Checkbox */}
      {selectionMode && (
        <View
          style={[
            styles.selectionBadge,
            {
              backgroundColor: isSelected ? tintColor : "transparent",
              borderColor: isSelected ? tintColor : borderColor,
            },
          ]}
        >
          {isSelected && <Ionicons name="checkmark" size={10} color="#FFFFFF" />}
        </View>
      )}

      {/* 1. Leading Thumbnail or Domain Icon */}
      {thumbnailUrl ? (
        <Image source={{ uri: thumbnailUrl }} style={styles.avatarThumbnail} resizeMode="cover" />
      ) : (
        <View style={[styles.avatarPlaceholder, { backgroundColor: badgeBg }]}>
          <Ionicons name={getEntityIcon(entityType, tagType)} size={16} color={tintColor} />
        </View>
      )}

      {/* 2. Text Column: Title + Optional Date/Meta */}
      <View style={styles.textColumn}>
        <ThemedText numberOfLines={date ? 1 : 2} type="defaultSemiBold" style={styles.titleText}>
          {displayTitle}
        </ThemedText>
        {date ? (
          <ThemedText numberOfLines={1} style={[styles.subText, { color: textMuted }]}>
            {date}
          </ThemedText>
        ) : null}
      </View>

      {/* 3. Platform Symbols + Context Actions */}
      <View style={styles.symbolsGroup}>
        {isSelf && (
          <ThemedPlatformSymbol type="you" size="small" />
        )}
        {isOwner && (
          <ThemedPlatformSymbol type="owner" size="small" />
        )}
        {isFiveStar && (
          <ThemedPlatformSymbol type="fivestar" size="small" />
        )}
        {kebabActions && kebabActions.length > 0 && (
          <ThemedKebabMenu
            actions={kebabActions}
            compact={true}
            accessibilityLabel={`Options for ${displayTitle}`}
          />
        )}
      </View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderRadius: 4,
    paddingHorizontal: 12,
    paddingVertical: 8,
    minHeight: 46,
    minWidth: 240,
    width: "100%",
    boxSizing: "border-box" as any,
    gap: 8,
  },
  selectionBadge: {
    width: 16,
    height: 16,
    borderRadius: 3,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  avatarThumbnail: {
    width: 32,
    height: 32,
    borderRadius: 4,
    flexShrink: 0,
  },
  avatarPlaceholder: {
    width: 32,
    height: 32,
    borderRadius: 4,
    justifyContent: "center",
    alignItems: "center",
    flexShrink: 0,
  },
  textColumn: {
    flex: 1,
    minWidth: 80,
    justifyContent: "center",
    overflow: "hidden",
  },
  titleText: {
    fontSize: 13,
    lineHeight: 18,
    flexShrink: 1,
  },
  subText: {
    fontSize: 11,
    lineHeight: 15,
    flexShrink: 1,
  },
  symbolsGroup: {
    flexDirection: "row",
    alignItems: "center",
    flexShrink: 0,
    gap: 4,
  },
  symbolIconWrapper: {
    padding: 2,
  },
  // Media Tier 2 compact card styles
  mediaCompactCard: {
    width: 90,
    height: 90,
    borderRadius: 4,
    borderWidth: 1,
    overflow: "hidden",
    position: "relative",
    margin: 1.5,
  },
  mediaThumbnail: {
    width: "100%",
    height: "100%",
  },
  mediaPlaceholder: {
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
  },
  mediaOverlayBottom: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: "rgba(0,0,0,0.55)",
    paddingHorizontal: 4,
    paddingVertical: 2,
  },
  mediaOverlayTitle: {
    fontSize: 10,
    color: "#FFFFFF",
    textAlign: "center",
  },
  mediaSymbolRow: {
    position: "absolute",
    top: 4,
    right: 4,
    flexDirection: "row",
    gap: 2,
  },
  miniSymbol: {
    borderRadius: 2,
    padding: 2,
  },
  // Share compact card styles
  shareItemSnippet: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    gap: 8,
  },
});

export default ThemedCompactCard;
